import { useEffect, useState } from "react";

import { friendlyTrpcError } from "../trpc-error";
import { trpc } from "../trpc";
import { cachedQuery } from "../cached-query";
import { useSelectedVertical } from "../use-selected-vertical";
import type { MasteryRow } from "./use-readiness";

interface UseHomeDataReturn {
  /** trpc.mastery.byTopic result for the active vertical. */
  mastery: MasteryRow[] | null;
  error: string | null;
  /** Friendly "Vertical · Jurisdiction" for the appbar title. */
  appbarTitle: string;
  /** True until the vertical hook has resolved. */
  isLoading: boolean;
  /** True when no vertical is selected — orchestrator routes to /welcome. */
  noVerticalSelected: boolean;
}

// Owns the fetch + appbar lookup for the home dashboard. Encapsulates
// the trpc roundtrip and the entitlement → friendly-name resolution.
export function useHomeData(): UseHomeDataReturn {
  const vertical = useSelectedVertical();
  const [mastery, setMastery] = useState<MasteryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (vertical.isLoading || !vertical.verticalSlug) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await cachedQuery(
          `mastery.byTopic:${vertical.verticalSlug}:${vertical.jurisdictionCode ?? ""}`,
          () =>
            trpc.mastery.byTopic.query({
              verticalSlug: vertical.verticalSlug!,
              // Scope attempt-derived stats to the active jurisdiction so a
              // multi-state vertical doesn't blend stats across states.
              ...(vertical.jurisdictionCode
                ? { jurisdictionCode: vertical.jurisdictionCode }
                : {}),
            }),
        );
        if (!cancelled) setMastery(rows);
      } catch (err) {
        // Never surface a raw tRPC code/message to the user (#10).
        if (!cancelled) setError(friendlyTrpcError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vertical.isLoading, vertical.verticalSlug, vertical.jurisdictionCode]);

  // AppBar title — "Vertical · Jurisdiction" per spec. Lookup via the
  // matching entitlement (which carries the friendly names) so it
  // automatically reflects the currently-selected vertical context.
  const current = vertical.entitlements.find(
    (e) =>
      e.verticalSlug === vertical.verticalSlug &&
      (e.jurisdictionCode ?? null) === vertical.jurisdictionCode,
  );
  const appbarTitle = current
    ? `${current.verticalName}${current.jurisdictionName ? ` · ${current.jurisdictionName}` : ""}`
    : (vertical.verticalSlug ?? "");

  return {
    mastery,
    error,
    appbarTitle,
    isLoading: vertical.isLoading || (mastery === null && error === null),
    noVerticalSelected: !vertical.isLoading && !vertical.verticalSlug,
  };
}
